if (!customElements.get('mobile-sticky-atc')) {
  customElements.define(
    'mobile-sticky-atc',
    class MobileStickyAtc extends HTMLElement {
      connectedCallback() {
        this.mql = window.matchMedia('(max-width: 749px)');
        this.submitButton = this.querySelector('[data-sticky-submit]');
        this.quantityValue = this.querySelector('[data-sticky-qty-value]');
        this.quantityDecrease = this.querySelector('[data-sticky-qty-decrease]');
        this.quantityIncrease = this.querySelector('[data-sticky-qty-increase]');
        this.productInfo = document.getElementById(this.dataset.productInfoId);
        this.priceSource = document.getElementById(this.dataset.priceId);
        this.sourceArea = this.productInfo?.querySelector('.product-form__buttons');

        if (!this.submitButton || !this.productInfo || !this.sourceArea) return;

        this.onClick = this.handleClick.bind(this);
        this.onMediaChange = this.handleMediaChange.bind(this);
        this.onScrollFallback = this.handleScroll.bind(this);
        this.onQuantityDecrease = () => this.stepQuantity(-1);
        this.onQuantityIncrease = () => this.stepQuantity(1);
        this.onSourceQuantityChange = () => this.syncQuantity();
        this.submitButton.addEventListener('click', this.onClick);
        this.quantityDecrease?.addEventListener('click', this.onQuantityDecrease);
        this.quantityIncrease?.addEventListener('click', this.onQuantityIncrease);
        this.bindSourceQuantityInput();
        this.mql.addEventListener?.('change', this.onMediaChange);
        window.addEventListener('scroll', this.onScrollFallback, { passive: true });
        this.hasPassedSourceArea = false;

        this.variantChangeUnsubscriber = subscribe?.(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.dataset.sectionId) return;
          window.requestAnimationFrame(() => this.sync());
        });

        this.cartUpdateUnsubscriber = subscribe?.(PUB_SUB_EVENTS.cartUpdate, () => {
          this.setLoading(false);
          this.sync();
        });

        this.cartErrorUnsubscriber = subscribe?.(PUB_SUB_EVENTS.cartError, () => {
          this.setLoading(false);
          this.sync();
          this.scrollToProductInfo();
        });

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries[0]) return;
              this.updateVisibility();
            },
            { threshold: [0, 0.15] }
          );
          this.observer.observe(this.sourceArea);
        }

        this.sync();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.submitButton?.removeEventListener('click', this.onClick);
        this.quantityDecrease?.removeEventListener('click', this.onQuantityDecrease);
        this.quantityIncrease?.removeEventListener('click', this.onQuantityIncrease);
        this.sourceQuantityInput?.removeEventListener('input', this.onSourceQuantityChange);
        this.sourceQuantityInput?.removeEventListener('change', this.onSourceQuantityChange);
        this.mql?.removeEventListener?.('change', this.onMediaChange);
        window.removeEventListener('scroll', this.onScrollFallback);
        if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
        this.observer?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
      }

      handleMediaChange() {
        this.updateVisibility();
      }

      handleScroll() {
        if (this.scrollFrame) return;

        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateVisibility();
        });
      }

      get sourceButton() {
        if (this.dataset.personalizable === 'true') {
          return this.productInfo?.querySelector(`[data-bjl-personalize-target="${this.dataset.personalizeTarget}"]`);
        }

        return document.getElementById(this.dataset.submitId);
      }

      bindSourceQuantityInput() {
        const nextInput = this.productInfo?.querySelector('input[name="quantity"]');

        if (nextInput === this.sourceQuantityInput) return;

        this.sourceQuantityInput?.removeEventListener('input', this.onSourceQuantityChange);
        this.sourceQuantityInput?.removeEventListener('change', this.onSourceQuantityChange);
        this.sourceQuantityInput = nextInput;
        this.sourceQuantityInput?.addEventListener('input', this.onSourceQuantityChange);
        this.sourceQuantityInput?.addEventListener('change', this.onSourceQuantityChange);
      }

      getQuantityBounds() {
        const input = this.sourceQuantityInput;
        const min = Number(input?.getAttribute('min') || input?.dataset.min || 1);
        const maxAttr = input?.getAttribute('max') || input?.dataset.max;
        const max = maxAttr ? Number(maxAttr) : null;
        const step = Number(input?.getAttribute('step') || 1);

        return {
          min: Number.isFinite(min) ? min : 1,
          max: Number.isFinite(max) ? max : null,
          step: Number.isFinite(step) && step > 0 ? step : 1,
        };
      }

      getCurrentQuantity() {
        const { min } = this.getQuantityBounds();
        const value = Number(this.sourceQuantityInput?.value || min);

        return Number.isFinite(value) ? value : min;
      }

      setQuantity(value) {
        if (!this.sourceQuantityInput) return;

        const { min, max } = this.getQuantityBounds();
        let nextValue = Math.max(min, value);

        if (max !== null) {
          nextValue = Math.min(max, nextValue);
        }

        this.sourceQuantityInput.value = String(nextValue);
        this.sourceQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.sourceQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncQuantity();
      }

      stepQuantity(direction) {
        const { step } = this.getQuantityBounds();
        this.setQuantity(this.getCurrentQuantity() + step * direction);
      }

      syncQuantity() {
        if (!this.quantityValue) return;

        const { min, max } = this.getQuantityBounds();
        const currentQuantity = this.getCurrentQuantity();

        this.quantityValue.textContent = currentQuantity;
        if (this.quantityDecrease) {
          this.quantityDecrease.disabled = currentQuantity <= min;
        }
        if (this.quantityIncrease) {
          this.quantityIncrease.disabled = max !== null && currentQuantity >= max;
        }
      }

      sync() {
        this.bindSourceQuantityInput();
        this.syncQuantity();

        const sourceButton = this.sourceButton;
        const sourceLabel = sourceButton?.querySelector('span')?.textContent?.trim() || this.dataset.defaultLabel || '';
        const stickyLabel =
          sourceLabel === this.dataset.defaultLabel ? this.dataset.stickyAddLabel || sourceLabel : sourceLabel;
        const disabled =
          !sourceButton ||
          sourceButton.disabled ||
          sourceButton.getAttribute('disabled') === 'disabled' ||
          sourceButton.getAttribute('aria-disabled') === 'true';

        this.submitButton.textContent = stickyLabel;
        this.submitButton.disabled = disabled;
      }

      updateVisibility() {
        if (this.sourceArea) {
          const rect = this.sourceArea.getBoundingClientRect();
          this.hasPassedSourceArea = rect.bottom <= 0;
        }

        const shouldShow = this.mql.matches && this.hasPassedSourceArea;

        this.classList.toggle('is-visible', !!shouldShow);
      }

      setLoading(isLoading) {
        this.submitButton.disabled = isLoading || this.submitButton.disabled;
        this.submitButton.classList.toggle('loading', isLoading);
        if (!isLoading) {
          this.sync();
        }
      }

      scrollToProductInfo() {
        this.productInfo?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      handleClick() {
        const sourceButton = this.sourceButton;

        if (!sourceButton) {
          this.scrollToProductInfo();
          return;
        }

        const disabled =
          sourceButton.disabled ||
          sourceButton.getAttribute('disabled') === 'disabled' ||
          sourceButton.getAttribute('aria-disabled') === 'true';

        if (disabled) {
          this.scrollToProductInfo();
          const variantSelect = this.productInfo.querySelector('variant-selects select, variant-selects input');
          variantSelect?.focus?.({ preventScroll: true });
          return;
        }

        this.setLoading(true);
        sourceButton.click();
        window.setTimeout(() => this.setLoading(false), 2500);
      }
    }
  );
}
